import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatarData } from "../lib/dados";
import {
  PERGUNTAS,
  avaliarFerramenta,
  lerMinhaAvaliacao,
  type CampoDaNota,
  type MinhaAvaliacao,
} from "../lib/avaliacoes";
import { Recado } from "./ui";

/**
 * O botão «Avaliar esta ferramenta» (000326, F9) e o formulário que ele abre.
 *
 * É um BOTÃO, e não um pop-up que abre sozinho como o das ferramentas da
 * Lovable (8 segundos depois do resultado, no Leitor): a decisão de Victor em
 * 29/09. Quem avalia é quem quis avaliar, e o aluno no meio de uma tarefa não é
 * interrompido.
 *
 * O formulário só é MONTADO no clique. É ele que lê a avaliação que o aluno já
 * deu, e abrir a ferramenta não paga essa ida ao servidor.
 */
export default function BotaoAvaliarFerramenta({
  card,
  nome,
  sobreNavy = false,
  className = "",
}: {
  card: string;
  nome: string;
  /**
   * Na coluna das conversas da IA, que é território navy nos DOIS temas: ali
   * token semântico é armadilha (a cicatriz dos cards em escada do login), e o
   * botão usa as cores de marca que o trilho já usa.
   */
  sobreNavy?: boolean;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const cores = sobreNavy
    ? "border-navy-ink/25 text-navy-ink hover:bg-navy-ink/10 focus-visible:ring-navy-ink"
    : "border-borda bg-superficie text-link hover:border-acento focus-visible:ring-acento";
  return (
    <>
      <button
        type="button"
        data-avaliar-ferramenta={card}
        onClick={() => setAberto(true)}
        className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 ${cores} ${className}`}
      >
        <Estrela />
        Avaliar esta ferramenta
      </button>
      {/* Num PORTAL, fora de onde o botão mora: o diálogo desenha no top layer,
          mas herda cor e as regras de território dos ancestrais do DOM — e o
          botão pode estar dentro do trilho navy. */}
      {aberto &&
        createPortal(<FormularioDeAvaliacao card={card} nome={nome} aoFechar={() => setAberto(false)} />, document.body)}
    </>
  );
}

function Estrela() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5Z" />
    </svg>
  );
}

type Estado =
  | { fase: "carregando" }
  | { fase: "erro"; detalhe: string }
  | { fase: "sem-acesso" }
  | { fase: "pronto"; atual: MinhaAvaliacao | null };

const VAZIAS: Record<CampoDaNota, number | null> = { utilidade: null, facilidade: null, satisfacao: null };

/**
 * `<dialog>` nativo, pela razão de `ModalVigencias`: Esc, a armadilha de foco e
 * o backdrop vêm de graça. O `m-auto` não é enfeite — o reset da casa zera a
 * margem, e sem ela o diálogo abre no canto (a cicatriz do Mapa de Concursos).
 * O cabeçalho e o botão de enviar ficam presos, e só o meio rola: numa tela
 * baixa, o botão não sai do alcance.
 */
function FormularioDeAvaliacao({ card, nome, aoFechar }: { card: string; nome: string; aoFechar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  const idComentario = useId();
  const [estado, setEstado] = useState<Estado>({ fase: "carregando" });
  const [notas, setNotas] = useState<Record<CampoDaNota, number | null>>(VAZIAS);
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (dlg && !dlg.open) dlg.showModal();
  }, []);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerMinhaAvaliacao(card);
      if (!vivo) return;
      if (!r.ok) {
        setEstado({ fase: "erro", detalhe: r.detalhe });
        return;
      }
      if (!r.pode_avaliar) {
        setEstado({ fase: "sem-acesso" });
        return;
      }
      setEstado({ fase: "pronto", atual: r.avaliacao });
      if (r.avaliacao) {
        setNotas({ utilidade: r.avaliacao.utilidade, facilidade: r.avaliacao.facilidade, satisfacao: r.avaliacao.satisfacao });
        setComentario(r.avaliacao.comentario ?? "");
      }
    })();
    return () => {
      vivo = false;
    };
  }, [card]);

  const atual = estado.fase === "pronto" ? estado.atual : null;
  const completas = PERGUNTAS.every((p) => notas[p.campo] !== null);
  // Espelha a regra do banco só para AVISAR antes do clique: quem decide é a
  // função, e a frase da recusa é a dela.
  const notaBaixa = PERGUNTAS.some((p) => (notas[p.campo] ?? 10) < 5);
  const faltaComentario = notaBaixa && comentario.trim() === "";
  const mudou =
    !atual ||
    PERGUNTAS.some((p) => notas[p.campo] !== atual[p.campo]) ||
    comentario.trim() !== (atual.comentario ?? "");
  const podeEnviar = estado.fase === "pronto" && completas && !faltaComentario && mudou && !enviando;

  async function enviar() {
    if (!podeEnviar) return;
    setEnviando(true);
    setRecado(null);
    const r = await avaliarFerramenta(card, notas, comentario);
    setEnviando(false);
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: r.detalhe ?? (r.ok ? "avaliação registrada." : "a avaliação não foi gravada.") });
    if (r.ok && r.avaliacao) setEstado({ fase: "pronto", atual: r.avaliacao });
  }

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close();
      }}
      aria-labelledby={idTitulo}
      data-formulario-de-avaliacao={card}
      className="m-auto w-[min(94vw,40rem)] rounded-[1.125rem] bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex max-h-[min(92dvh,52rem)] flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-borda p-5 sm:p-6">
          <div className="min-w-0">
            {/* O nome fica FORA da frase: ele é escolhido pela equipe, e "Avalie
                o …" erraria o artigo em "Central Inteligente" e "Calculadoras"
                (a cicatriz da 000308 — frase não concorda com nome que o usuário
                define). */}
            <p className="text-sm font-semibold text-link" data-nome-da-ferramenta>
              {nome}
            </p>
            <h2 id={idTitulo} className="mt-0.5 font-display text-xl font-bold text-titulo">
              Avalie esta ferramenta
            </h2>
            {/* A promessa vem ANTES do formulário, porque decide o que a pessoa
                escreve — a mesma ordem da página de Avaliações das pesquisas. */}
            <p className="mt-1 text-sm leading-relaxed text-cinza">
              As notas e o comentário vão para a equipe de Produtos, que vê o que foi dito, mas não quem
              disse.
            </p>
          </div>
          <button
            type="button"
            onClick={() => ref.current?.close()}
            aria-label="Fechar"
            className="grid size-11 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="size-5" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          {estado.fase === "carregando" ? (
            <p className="text-sm text-cinza">Carregando…</p>
          ) : estado.fase === "erro" ? (
            <p className="text-sm text-cinza" role="alert">
              {estado.detalhe}
            </p>
          ) : estado.fase === "sem-acesso" ? (
            <p className="text-sm text-cinza" data-avaliacao-sem-acesso>
              Esta ferramenta não está entre as que você tem hoje, e por isso não dá para avaliá-la.
            </p>
          ) : (
            <>
              {atual && (
                <p className="mb-4 rounded-xl bg-chip px-3.5 py-2.5 text-sm text-tinta" data-avaliacao-anterior>
                  Você avaliou esta ferramenta em {formatarData(atual.atualizada_em)}. Pode mudar quando quiser: vale
                  a avaliação mais recente.
                </p>
              )}
              <div className="space-y-5">
                {PERGUNTAS.map((p) => (
                  <Escala
                    key={p.campo}
                    campo={p.campo}
                    texto={p.texto}
                    valor={notas[p.campo]}
                    aoEscolher={(n) => setNotas((atuais) => ({ ...atuais, [p.campo]: n }))}
                  />
                ))}
              </div>

              <label htmlFor={idComentario} className="mt-5 block text-sm font-semibold text-tinta">
                O que você melhoraria na ferramenta?{" "}
                <span className={notaBaixa ? "font-semibold text-alerta-tinta" : "font-normal text-cinza"} data-comentario-pedido={notaBaixa ? "sim" : "nao"}>
                  {notaBaixa ? "(obrigatório: há nota abaixo de 5)" : "(opcional)"}
                </span>
              </label>
              <textarea
                id={idComentario}
                value={comentario}
                onChange={(e) => setComentario(e.target.value.slice(0, 1000))}
                maxLength={1000}
                rows={3}
                aria-required={notaBaixa}
                placeholder={notaBaixa ? "Conte o que motivou a nota abaixo de 5." : "Conte como podemos melhorar."}
                className="mt-1.5 w-full rounded-xl border border-borda bg-superficie p-3 text-sm text-tinta placeholder:text-cinza"
                data-comentario
              />
              <p className="mt-1 text-right text-xs tabular-nums text-cinza" aria-live="polite">
                {comentario.length.toLocaleString("pt-BR")} de 1.000 caracteres
              </p>
            </>
          )}
        </div>

        {estado.fase === "pronto" && (
          <div className="border-t border-borda p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void enviar()}
                disabled={!podeEnviar}
                className={podeEnviar ? "pill-cta" : "pill-desligada"}
                data-enviar-avaliacao
              >
                {enviando ? "Enviando…" : atual ? "Atualizar avaliação" : "Enviar avaliação"}
              </button>
              <button
                type="button"
                onClick={() => ref.current?.close()}
                className="text-sm font-semibold text-link underline-offset-2 hover:underline"
              >
                Fechar
              </button>
              {!completas && (
                <span className="text-xs text-cinza" data-falta-nota>
                  Dê uma nota às três perguntas.
                </span>
              )}
            </div>
            {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
          </div>
        )}
      </div>
    </dialog>
  );
}

/**
 * Uma pergunta: dez RADIOS nativos desenhados como botões — o desenho do
 * `FormularioDeNota` das pesquisas. As setas do teclado e o "3 de 10,
 * selecionado" do leitor de tela vêm sem uma linha de ARIA.
 *
 * Grade de dez colunas, e não `flex-wrap`: no celular a linha de dez não cabe
 * com o tamanho fixo e quebraria em duas, e a escala partida no meio não se lê
 * como escala.
 */
function Escala({
  campo,
  texto,
  valor,
  aoEscolher,
}: {
  campo: CampoDaNota;
  texto: string;
  valor: number | null;
  aoEscolher: (n: number) => void;
}) {
  const idRotulo = useId();
  return (
    <div role="radiogroup" aria-labelledby={idRotulo} data-escala={campo}>
      <p id={idRotulo} className="text-sm font-semibold text-tinta">
        {texto}
      </p>
      <div className="mt-2 grid grid-cols-10 gap-1 sm:gap-1.5">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <label key={n} className="relative">
            <input
              type="radio"
              name={`avaliacao-${campo}`}
              value={n}
              checked={valor === n}
              onChange={() => aoEscolher(n)}
              className="peer sr-only"
              data-nota={n}
            />
            <span className="grid h-10 cursor-pointer place-items-center rounded-lg border border-borda bg-superficie text-sm font-semibold tabular-nums text-tinta transition peer-checked:border-acento peer-checked:bg-acento peer-checked:text-acento-tinta peer-focus-visible:ring-2 peer-focus-visible:ring-acento hover:border-acento">
              {n}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-xs text-cinza" aria-hidden="true">
        <span>1 — muito pouco</span>
        <span>10 — muito</span>
      </div>
    </div>
  );
}
